/** Thin typed client for the server. Every request goes to /api; the session token rides in Authorization. */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly retryAfterSeconds?: number,
    /** The whole error body, for codes that carry data (e.g. confirm-quantities → warnings). */
    readonly body: unknown = null,
  ) {
    super(message);
  }
}

let token: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setToken(t: string | null): void {
  token = t;
}
/** Called when the server says the session is over (idle lock): the app shows the PIN pad again. */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: init.method ?? (init.body === undefined ? "GET" : "POST"),
    headers: {
      ...(init.body === undefined ? {} : { "content-type": "application/json" }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
  });
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const b = (data ?? {}) as { code?: string; message?: string | string[]; retryAfterSeconds?: number };
    const message = Array.isArray(b.message)
      ? b.message.join("، ")
      : (b.message ?? "خطایی پیش آمد؛ دوباره امتحان کنید");
    if (res.status === 401 && token && b.code === "session-invalid") onUnauthorized?.();
    throw new ApiError(res.status, b.code ?? "error", message, b.retryAfterSeconds, data);
  }
  return data as T;
}

// ---------- shapes returned by the server (see apps/server controllers) ----------

export interface Enrolment {
  deviceId: string;
  deviceSecret: string;
  deviceStatus: "active" | "pending-approval";
  personId: string;
  displayName: string;
  needsPin: boolean;
  pinLength: 4 | 6;
  orderingUnitIds: string[];
}
export interface Me {
  personId: string;
  deviceId: string;
  context: "customer" | "staff";
}
export interface CatalogEntry {
  module: string;
  moduleLabel: string;
  resource: string;
  label: string;
  actions: ("view" | "create" | "update" | "approve")[];
  audience: "staff" | "customer" | "both";
}
export interface RoleRow {
  id: string;
  version: number;
  systemKey: string | null;
  name: string;
  description: string;
  context: "customer" | "staff";
  sensitive: boolean;
  grantsAll: boolean;
  permissions: { resource: string; actions: CatalogEntry["actions"] }[];
  scope: { kind: string; ids?: string[] };
  switches: { seePrices: boolean; seeCost: boolean; addOrderLines: boolean };
  activeAssignments: number;
}
export interface TreeUnit {
  id: string;
  version: number;
  name: string;
  code: string | null;
  contacts: number;
}
export interface TreeNode {
  id: string;
  version: number;
  name: string;
  code: string | null;
  children: TreeNode[];
  units: TreeUnit[];
}
export interface ContactRow {
  id: string;
  version: number;
  name: string;
  phone: string;
  isPrimary: boolean;
  linked: boolean;
}
export interface SiteRow {
  id: string;
  version: number;
  name: string;
  kind: "kitchen" | "hall" | "serving-point";
  active: boolean;
  stations: { id: string; version: number; name: string; sortOrder: number }[];
}
export interface SettingRow {
  key: string;
  label: string;
  default: unknown;
  value: unknown;
  /** Version of the global value, null when only the default applies. */
  version: number | null;
}
export interface StaffRow {
  id: string;
  displayName: string;
  phone: string;
  hasPin: boolean;
}

export interface WarehouseRow {
  id: string;
  version: number;
  kitchenId: string | null;
  code: string;
  name: string;
  isVirtual: boolean;
  status: "active" | "archived";
}

export interface StockItemRow {
  id: string;
  version: number;
  code: string;
  name: string;
  category: string;
  baseUnit: string;
  minStockMilli: number;
  componentId: string | null;
  status: "active" | "archived";
}

export interface StockBalanceRow {
  warehouseId: string;
  itemId: string;
  quantityMilli: number;
  totalValueRial: number;
  averageUnitCostRial: number;
}

export interface VoucherLineRow {
  id?: string;
  itemId: string;
  quantityMilli: number;
  unitPriceRial?: number | null;
  note?: string | null;
}

export interface VoucherRow {
  id: string;
  version: number;
  voucherNumber?: string | null;
  year?: number | null;
  number?: number | null;
  date: string;
  warehouseId: string;
  kind:
    | "receipt"
    | "issue"
    | "return_to_supplier"
    | "waste"
    | "count"
    | "transfer_send"
    | "transfer_receive"
    | "staff_meal"
    | "staff-meal";
  status: "draft" | "posted";
  targetWarehouseId?: string | null;
  transferId?: string | null;
  shortageLines?: { itemId: string; shortageMilli: number; reason?: string }[];
  reason?: string | null;
  reversalOfVoucherId?: string | null;
  lines: VoucherLineRow[];
}

export interface KardexRow {
  voucherId: string;
  voucherNumber: string;
  date: string;
  kind: VoucherRow["kind"];
  inQuantityMilli: number;
  inUnitPriceRial: number;
  inTotalRial: number;
  outQuantityMilli: number;
  outUnitPriceRial: number;
  outTotalRial: number;
  balanceQuantityMilli: number;
  balanceAverageCostRial: number;
  balanceTotalValueRial: number;
}

export interface BomLineRow {
  id?: string;
  itemId: string;
  quantityPerPortionMilli: number;
  wasteFactorMilli: number;
}

export interface BomVersionRow {
  id: string;
  version: number;
  componentId?: string | null;
  menuItemId?: string | null;
  tierId?: string | null;
  validFrom: string;
  lines: BomLineRow[];
}

export interface ShortageItemRow {
  warehouseId: string;
  itemId: string;
  neededMilli: number;
  currentBalanceMilli: number;
  shortageMilli: number;
  estimatedCostRial: number;
}

export interface ShortageForecastRow {
  date: string;
  kitchenId: string;
  items: ShortageItemRow[];
  totalEstimatedCostRial: number;
}

// ---------- Distribution (AG-22) ----------
import type {
  ContainerKardex,
  ContainerType,
  ContainerTypeInput,
  DeliveryProblem,
  Dispatch,
  DispatchStop,
  DriverDayReport,
  DriverDispatchView,
  DriverStopView,
  RecordContainerEntryInput,
  RecordDeliveryInput,
  RecordDeliveryProblemInput,
  RouteTemplate,
  RouteTemplateInput,
  UnitContainerBalance,
} from "@kitchen/schemas";

export type RouteRow = RouteTemplate & { version: number };
export type DispatchRow = Dispatch & { version: number };
export type ContainerTypeRow = ContainerType & { version: number };
export type DriverDispatchRow = DriverDispatchView & { version?: number };

export const distributionApi = {
  listRoutes: (mealId?: string) =>
    api<RouteRow[]>(`/distribution/routes${mealId ? `?mealId=${mealId}` : ""}`),
  getRoute: (id: string) => api<RouteRow>(`/distribution/routes/${id}`),
  createRoute: (body: RouteTemplateInput) => api<RouteRow>("/distribution/routes", { body }),
  updateRoute: (id: string, version: number, body: Partial<RouteTemplateInput>) =>
    api<RouteRow>(`/distribution/routes/${id}`, { method: "PUT", body: { ...body, version } }),
  archiveRoute: (id: string, version: number, reason: string) =>
    api<{ ok: boolean }>(`/distribution/routes/${id}/archive`, { body: { version, reason } }),

  listDispatches: (date: string, mealId?: string) =>
    api<DispatchRow[]>(`/distribution/dispatch?date=${date}${mealId ? `&mealId=${mealId}` : ""}`),
  getDispatch: (id: string) => api<DispatchRow>(`/distribution/dispatch/${id}`),
  createDispatchFromTemplate: (templateId: string, businessDate: string) =>
    api<DispatchRow>("/distribution/dispatch/from-template", { body: { templateId, businessDate } }),
  moveStop: (dispatchId: string, stopId: string, version: number, newSequence: number) =>
    api<DispatchRow>(`/distribution/dispatch/${dispatchId}/move-stop/${stopId}`, {
      body: { version, newSequence },
    }),
  swapDriver: (dispatchId: string, version: number, newDriverId: string) =>
    api<DispatchRow>(`/distribution/dispatch/${dispatchId}/swap-driver`, { body: { version, newDriverId } }),
  reassignStop: (dispatchId: string, stopId: string, version: number, targetDispatchId: string) =>
    api<{ ok: boolean }>(`/distribution/dispatch/${dispatchId}/reassign-stop/${stopId}`, {
      body: { version, targetDispatchId },
    }),
  confirmLoading: (dispatchId: string, version: number) =>
    api<{ version: number }>(`/distribution/dispatch/${dispatchId}/confirm-loading`, { body: { version } }),
  getReport: (dispatchId: string) => api<DriverDayReport>(`/distribution/dispatch/${dispatchId}/report`),

  // Driver phone endpoints
  getDriverDispatch: (date?: string, mealId?: string) =>
    api<DriverDispatchRow[]>(
      `/distribution/driver/dispatch?${date ? `date=${date}` : ""}${mealId ? `&mealId=${mealId}` : ""}`,
    ),
  driverConfirmLoading: (dispatchId: string, version: number) =>
    api<{ version: number }>(`/distribution/driver/confirm-loading/${dispatchId}`, { body: { version } }),
  recordStopDelivery: (dispatchId: string, stopId: string, version: number, body: RecordDeliveryInput) =>
    api<DispatchStop>(`/distribution/driver/stop-delivery/${dispatchId}/${stopId}`, {
      body: { ...body, version },
    }),
  driverRecordProblem: (body: RecordDeliveryProblemInput) =>
    api<DeliveryProblem>("/distribution/driver/problems", { body }),

  // Problems & Deliveries (Manager)
  recordDelivery: (dispatchId: string, stopId: string, version: number, body: RecordDeliveryInput) =>
    api<{ version: number; stop: DriverStopView }>(`/distribution/deliveries/${dispatchId}/${stopId}`, {
      body: { ...body, version },
    }),
  recordProblem: (body: RecordDeliveryProblemInput) =>
    api<DeliveryProblem>("/distribution/deliveries/problems", { body }),
  listProblems: (date?: string, unitId?: string) =>
    api<DeliveryProblem[]>(
      `/distribution/deliveries/problems?${date ? `date=${date}` : ""}${unitId ? `&unitId=${unitId}` : ""}`,
    ),
  resolveProblem: (id: string, version: number, resolutionNote: string) =>
    api<DeliveryProblem>(`/distribution/deliveries/problems/${id}/resolve`, {
      body: { version, resolutionNote },
    }),

  // Containers & Kardex
  listContainerTypes: () => api<ContainerTypeRow[]>("/distribution/containers/types"),
  createContainerType: (body: ContainerTypeInput) =>
    api<ContainerTypeRow>("/distribution/containers/types", { body }),
  updateContainerType: (id: string, version: number, body: Partial<ContainerTypeInput>) =>
    api<ContainerTypeRow>(`/distribution/containers/types/${id}`, {
      method: "PUT",
      body: { ...body, version },
    }),
  recordContainerEntry: (body: RecordContainerEntryInput) =>
    api<ContainerKardex>("/distribution/containers/entries", { body }),
  reverseContainerEntry: (id: string, reason: string) =>
    api<ContainerKardex>(`/distribution/containers/entries/${id}/reverse`, { body: { reason } }),
  getKardex: (unitId: string, containerTypeId: string, asOfDate?: string) =>
    api<ContainerKardex>(
      `/distribution/containers/kardex/${unitId}/${containerTypeId}${asOfDate ? `?asOfDate=${asOfDate}` : ""}`,
    ),
  listBalances: (asOfDate?: string) =>
    api<UnitContainerBalance[]>(
      `/distribution/containers/balances${asOfDate ? `?asOfDate=${asOfDate}` : ""}`,
    ),
};

export interface StaffMealRow {
  id: string;
  businessDate: string;
  kitchenId: string;
  mealId: string;
  menuItemId: string;
  portions: number;
  voucherId: string;
  note?: string | null;
  version?: number;
  createdAt?: string;
}

export interface StaffMealInput {
  businessDate: string;
  kitchenId: string;
  mealId: string;
  menuItemId: string;
  portions: number;
  note?: string;
}

export interface CostSettingsRow {
  fixedMode: "per_portion" | "monthly_share";
  packagingCostRial: number;
  breadCostRial: number;
  staffSharePerPortionRial: number;
  overheadSharePerPortionRial: number;
  monthlyStaffPoolRial: number;
  monthlyOverheadPoolRial: number;
  monthlyPortionsEstimate: number;
  reconciliationThresholdPerMille: number;
}

export interface CostSnapshotRow {
  id: string;
  businessDate: string;
  menuItemId: string;
  tierId?: string | null;
  materialsCostRial: number;
  packagingCostRial: number;
  breadCostRial: number;
  fixedShareRial: number;
  totalCostRial: number;
  fixedMode: "per_portion" | "monthly_share";
  details?: Record<string, unknown>;
  frozenAt?: string;
  version?: number;
  createdAt?: string;
}

export interface ReconciliationItemRow {
  businessDate: string;
  kitchenId: string;
  menuItemId: string;
  foodName: string;
  plannedPortions: number;
  producedPortions: number;
  deliveredPortions: number;
  staffMealPortions: number;
  differencePortions: number;
  thresholdPerMille: number;
  exceedsThreshold: boolean;
}

export interface ReconciliationReportRow {
  businessDate: string;
  kitchenId: string;
  items: ReconciliationItemRow[];
}
// ---------- Finance (AG-26) ----------

import type {
  AllocatePaymentInput,
  ChequeActionInput,
  CreateInvoiceDraftInput,
  CustomerStatement,
  DebitCreditNote,
  Invoice,
  InvoiceBasis,
  InvoiceStatus,
  IssueInvoiceInput,
  PaymentInput,
  PaymentRecord,
  RecomputeDraftInput,
  RePricingImpactPreview,
  RePricingPreviewInput,
  ReviseInvoiceInput,
} from "@kitchen/schemas";

export const financeApi = {
  // Settings
  getBasis: (organizationId: string) =>
    api<{ organizationId: string; basis: InvoiceBasis }>(`/finance/settings/basis/${organizationId}`),
  setBasis: (organizationId: string, basis: InvoiceBasis) =>
    api<{ organizationId: string; basis: InvoiceBasis }>(`/finance/settings/basis/${organizationId}`, {
      method: "PUT",
      body: { basis },
    }),
  setHighDebtThreshold: (organizationId: string, thresholdRials: number) =>
    api<{ organizationId: string; thresholdRials: number }>(`/finance/settings/high-debt/${organizationId}`, {
      method: "PUT",
      body: { thresholdRials },
    }),

  // Invoices
  listInvoices: (query?: {
    organizationId?: string | undefined;
    status?: InvoiceStatus | undefined;
    periodStart?: string | undefined;
    periodEnd?: string | undefined;
  }) => {
    const params = new URLSearchParams();
    if (query?.organizationId) params.set("organizationId", query.organizationId);
    if (query?.status) params.set("status", query.status);
    if (query?.periodStart) params.set("periodStart", query.periodStart);
    if (query?.periodEnd) params.set("periodEnd", query.periodEnd);
    const qs = params.toString();
    return api<Invoice[]>(`/finance/invoices${qs ? `?${qs}` : ""}`);
  },
  getInvoice: (id: string) => api<Invoice & { version: number }>(`/finance/invoices/${id}`),
  createDraft: (body: CreateInvoiceDraftInput) =>
    api<{ invoice: Invoice; unpricedLines: unknown[]; version: number }>("/finance/invoices/draft", { body }),
  recomputeDraft: (id: string, body: RecomputeDraftInput) =>
    api<{ invoice: Invoice; unpricedLines: unknown[]; version: number }>(
      `/finance/invoices/${id}/recompute`,
      {
        body,
      },
    ),
  issueInvoice: (id: string, body: IssueInvoiceInput) =>
    api<{ invoice: Invoice; version: number }>(`/finance/invoices/${id}/issue`, { body }),
  reviseInvoice: (id: string, body: ReviseInvoiceInput) =>
    api<{
      newRevision: Invoice;
      note: DebitCreditNote | null;
      unpricedLines: unknown[];
      newInvoiceId: string;
      noteId: string | null;
    }>(`/finance/invoices/${id}/revise`, { body }),
  previewRePricing: (body: RePricingPreviewInput) =>
    api<RePricingImpactPreview>("/finance/re-pricing-preview", { body }),

  // Statement & Aging
  getStatement: (organizationId: string, asOfDate?: string) =>
    api<CustomerStatement>(`/finance/statement/${organizationId}${asOfDate ? `?asOfDate=${asOfDate}` : ""}`),

  // Payments
  listPayments: (organizationId?: string) =>
    api<PaymentRecord[]>(`/finance/payments${organizationId ? `?organizationId=${organizationId}` : ""}`),
  recordPayment: (body: PaymentInput) => api<PaymentRecord>("/finance/payments", { body }),
  updateChequeStatus: (id: string, body: ChequeActionInput) =>
    api<PaymentRecord>(`/finance/payments/${id}/cheque-status`, { body }),
  allocatePayment: (body: AllocatePaymentInput) =>
    api<{ version: number; allocatedCount: number }>("/finance/payments/allocate", { body }),
};

// ---------- F9 Forecast & Dashboard ----------

export interface TomorrowFoodEstimateRow {
  foodId: string;
  foodName?: string;
  expectedPortions: number;
  orderedPortions?: number;
  foodSharePerMille: number;
}

export interface TomorrowEstimateRow {
  targetDate: string;
  weekday: number;
  weekdayName: string;
  mealId: string;
  kitchenId?: string;
  totalExpectedPortions: number;
  totalOrderedPortions?: number;
  lowPortions: number;
  highPortions: number;
  foods: TomorrowFoodEstimateRow[];
  participatingUnitsCount: number;
  explanation: string;
}

export interface OrderDropAlertRow {
  id: string;
  unitId: string;
  unitCode?: string;
  unitName?: string;
  organizationName?: string;
  severity: "important" | "critical";
  dropPerMille: number;
  recentAveragePortions: number;
  usualPortions: number;
  recentSamples: number[];
  reason: string;
  detectedDate: string;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  reviewNote?: string | null;
  createdAt: string;
}

export interface UnitTrendRow {
  unitId: string;
  unitName?: string;
  organizationName?: string;
  mealId: string;
  recent4WeeksPortions: number;
  prior4WeeksPortions: number;
  trendPerMille: number | null;
  sampleCountRecent: number;
  sampleCountPrior: number;
  explanation: string;
}

export interface ForecastAccuracyItemRow {
  businessDate: string;
  kitchenId?: string;
  expectedPortions: number;
  actualPortions: number;
  absoluteError: number;
}

export interface ForecastAccuracySummaryRow {
  kitchenId?: string;
  period: string;
  totalDays: number;
  meanAbsoluteError: number;
  explanation: string;
}

export interface ForecastHolidayRow {
  id: string;
  businessDate: string;
  title: string;
  createdAt: string;
  createdBy?: string | null;
}

export interface ManagerDashboardRow {
  todayOrders?: {
    error?: boolean;
    total: number;
    approved: number;
    submitted: number;
    inProduction: number;
    delivered: number;
  };
  tomorrowForecast?: {
    error?: boolean;
    expectedPortions: number;
    orderedPortions: number;
    lowPortions: number;
    highPortions: number;
  };
  dropAlerts?: {
    error?: boolean;
    openCount: number;
    criticalCount: number;
  };
  kitchenTasksLate?: {
    error?: boolean;
    lateCount: number;
  };
  deliveryExceptions?: {
    error?: boolean;
    shortOrFailedCount: number;
  };
  stockShortages?: {
    error?: boolean;
    underMinCount: number;
  };
}

// ---------- Profit Report (AG-35) ----------

import type {
  ProfitFoodRow,
  ProfitDayRow,
  OrganizationProfitSummary,
  ProfitPeriodSummary,
  ProfitReportResponse,
} from "@kitchen/schemas";

export async function getProfitReport(query?: {
  from?: string | undefined;
  to?: string | undefined;
  organizationId?: string | undefined;
}): Promise<ProfitReportResponse> {
  const params = new URLSearchParams();
  if (query?.from) params.set("from", query.from);
  if (query?.to) params.set("to", query.to);
  if (query?.organizationId) params.set("organizationId", query.organizationId);
  const qs = params.toString();
  return api<ProfitReportResponse>(`/profit${qs ? `?${qs}` : ""}`);
}

export const profitApi = {
  getProfitReport,
};

export type {
  ProfitFoodRow,
  ProfitDayRow,
  OrganizationProfitSummary,
  ProfitPeriodSummary,
  ProfitReportResponse,
};

// ---------- HR (AG-36) ----------

export interface PositionRow {
  id: string;
  version: number;
  name: string;
  description: string;
  checklist: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface DocumentTypeRow {
  id: string;
  version: number;
  code: string;
  name: string;
  description: string;
  active: boolean;
}

export interface PersonDocumentRow {
  id: string;
  version: number;
  personId: string;
  kind: string;
  kindLabel?: string;
  documentNumber: string;
  issueDate?: string | null;
  expiryDate: string;
  notes: string;
  notifiedStages: string[];
  expiryStatus?: "valid" | "expiring_soon" | "expired";
  daysUntilExpiry?: number;
}

export interface PersonnelFileRow {
  id: string;
  version: number;
  personId: string;
  personName: string;
  phone?: string;
  positionId?: string | null;
  positionName: string;
  level: string;
  employmentKind: "official" | "contractual" | "daily" | "temporary";
  startDate: string;
  emergencyContact: {
    name: string;
    phone: string;
    relation?: string;
  };
  basePay?: number | null;
  hourlyRate?: number | null;
  notes: string;
  documents?: PersonDocumentRow[];
}

export const hrApi = {
  // Positions
  listPositions: () => api<PositionRow[]>("/hr/positions"),
  getPositionDescription: (id: string) =>
    api<{ id: string; name: string; description: string; checklist: string[] }>(
      `/hr/positions/${id}/description`,
    ),
  createPosition: (body: {
    name: string;
    description?: string | undefined;
    checklist?: string[] | undefined;
  }) => api<{ id: string }>("/hr/positions", { body }),
  updatePosition: (
    id: string,
    body: {
      version: number;
      name?: string | undefined;
      description?: string | undefined;
      checklist?: string[] | undefined;
    },
  ) => api<{ version: number }>(`/hr/positions/${id}`, { method: "PUT", body }),
  archivePosition: (id: string, version: number, reason?: string) =>
    api<unknown>(`/hr/positions/${id}/archive`, { body: { version, reason: reason || "بایگانی توسط مدیر" } }),

  // Document Types
  listDocumentTypes: () => api<DocumentTypeRow[]>("/hr/document-types"),
  createDocumentType: (body: {
    code: string;
    name: string;
    description?: string | undefined;
    active?: boolean | undefined;
  }) => api<{ id: string }>("/hr/document-types", { body }),
  updateDocumentType: (
    id: string,
    body: {
      version: number;
      name?: string | undefined;
      description?: string | undefined;
      active?: boolean | undefined;
    },
  ) => api<{ version: number }>(`/hr/document-types/${id}`, { method: "PUT", body }),
  archiveDocumentType: (id: string, version: number, reason?: string) =>
    api<unknown>(`/hr/document-types/${id}/archive`, {
      body: { version, reason: reason || "بایگانی توسط مدیر" },
    }),

  // Personnel Files
  listPeople: () => api<PersonnelFileRow[]>("/hr/people"),
  getPersonFile: (id: string) => api<PersonnelFileRow>(`/hr/people/${id}`),
  createPersonnelFile: (body: {
    personId: string;
    personName?: string | undefined;
    positionId?: string | null | undefined;
    positionName?: string | undefined;
    level?: string | undefined;
    employmentKind: "official" | "contractual" | "daily" | "temporary";
    startDate: string;
    emergencyContact: { name: string; phone: string; relation?: string | undefined };
    basePay?: number | null | undefined;
    hourlyRate?: number | null | undefined;
    notes?: string | undefined;
  }) => api<{ id: string }>("/hr/people", { body }),
  updatePersonnelFile: (
    id: string,
    body: {
      version: number;
      personName?: string | undefined;
      positionId?: string | null | undefined;
      positionName?: string | undefined;
      level?: string | undefined;
      employmentKind?: "official" | "contractual" | "daily" | "temporary" | undefined;
      startDate?: string | undefined;
      emergencyContact?: { name: string; phone: string; relation?: string | undefined } | undefined;
      basePay?: number | null | undefined;
      hourlyRate?: number | null | undefined;
      notes?: string | undefined;
    },
  ) => api<{ version: number }>(`/hr/people/${id}`, { method: "PUT", body }),
  archivePersonnelFile: (id: string, version: number, reason?: string) =>
    api<unknown>(`/hr/people/${id}/archive`, { body: { version, reason: reason || "بایگانی توسط مدیر" } }),
  updatePersonPay: (
    id: string,
    body: {
      version: number;
      basePay?: number | null | undefined;
      hourlyRate?: number | null | undefined;
    },
  ) => api<{ version: number }>(`/hr/people/${id}/pay`, { method: "PUT", body }),

  // Documents
  listDocuments: (personId?: string) =>
    api<PersonDocumentRow[]>(`/hr/documents${personId ? `?personId=${personId}` : ""}`),
  createDocument: (body: {
    personId: string;
    kind: string;
    documentNumber: string;
    issueDate?: string | null | undefined;
    expiryDate: string;
    notes?: string | undefined;
  }) => api<{ id: string }>("/hr/documents", { body }),
  updateDocument: (
    id: string,
    body: {
      version: number;
      personId?: string | undefined;
      kind?: string | undefined;
      documentNumber?: string | undefined;
      issueDate?: string | null | undefined;
      expiryDate?: string | undefined;
      notes?: string | undefined;
    },
  ) => api<{ version: number }>(`/hr/documents/${id}`, { method: "PUT", body }),
  archiveDocument: (id: string, version: number, reason?: string) =>
    api<unknown>(`/hr/documents/${id}/archive`, { body: { version, reason: reason || "بایگانی توسط مدیر" } }),

  // Daily Job
  runExpiryJob: () => api<{ notifiedCount: number }>("/hr/jobs/check-expiries", { method: "POST" }),
};

// ---------- Daily Report (AG-38) ----------

import type {
  DailyReportResponse,
  DailyReportSales,
  DailyReportProfit,
  DailyReportStockUsed,
  DailyReportMoneyReceived,
} from "@kitchen/schemas";

export async function getDailyReport(date?: string): Promise<DailyReportResponse> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  return api<DailyReportResponse>(`/daily-report${qs}`);
}

export const dailyReportApi = {
  getDailyReport,
};

export type {
  DailyReportResponse,
  DailyReportSales,
  DailyReportProfit,
  DailyReportStockUsed,
  DailyReportMoneyReceived,
};

// ---------- HR Shifts & Attendance (AG-39) ----------

import type {
  AttendancePunch,
  DayShiftPlanItem,
  LeaveRecord,
  ProposePunchCorrectionInput,
  PunchCorrectionRequest,
  PunchInput,
  RecordLeaveInput,
  ReviewPunchCorrectionInput,
  ShiftDateException,
  ShiftDateExceptionInput,
  ShiftTemplate,
  ShiftTemplateInput,
  TodayAttendanceSummary,
  WeeklyPatternDay,
  WeeklyShiftPattern,
} from "@kitchen/schemas";

export async function listShiftTemplates(): Promise<ShiftTemplate[]> {
  return api<ShiftTemplate[]>("/hr/shifts");
}

export async function createShiftTemplate(body: ShiftTemplateInput): Promise<{ id: string }> {
  return api<{ id: string }>("/hr/shifts", { method: "POST", body });
}

export async function updateShiftTemplate(
  id: string,
  version: number,
  patch: Partial<ShiftTemplateInput>,
): Promise<{ version: number }> {
  return api<{ version: number }>(`/hr/shifts/${id}`, { method: "PUT", body: { version, ...patch } });
}

export async function archiveShiftTemplate(id: string, version: number, reason?: string): Promise<void> {
  await api<unknown>(`/hr/shifts/${id}/archive`, {
    body: { version, reason: reason || "بایگانی توسط مدیر" },
  });
}

export async function listWeeklyShiftPatterns(): Promise<WeeklyShiftPattern[]> {
  return api<WeeklyShiftPattern[]>("/hr/shifts/weekly-patterns");
}

export async function getWeeklyShiftPattern(personId: string): Promise<WeeklyShiftPattern | null> {
  return api<WeeklyShiftPattern | null>(`/hr/shifts/weekly-patterns/${personId}`);
}

export async function setWeeklyShiftPattern(
  personId: string,
  days: WeeklyPatternDay[],
): Promise<{ id: string; version: number }> {
  return api<{ id: string; version: number }>(`/hr/shifts/weekly-patterns/${personId}`, {
    method: "PUT",
    body: { days },
  });
}

export async function listShiftExceptions(fromDate?: string, toDate?: string): Promise<ShiftDateException[]> {
  const params = new URLSearchParams();
  if (fromDate) params.set("fromDate", fromDate);
  if (toDate) params.set("toDate", toDate);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return api<ShiftDateException[]>(`/hr/shifts/exceptions${qs}`);
}

export async function createShiftException(body: ShiftDateExceptionInput): Promise<{ id: string }> {
  return api<{ id: string }>("/hr/shifts/exceptions", { method: "POST", body });
}

export async function archiveShiftException(id: string, version: number, reason?: string): Promise<void> {
  await api<unknown>(`/hr/shifts/exceptions/${id}/archive`, {
    body: { version, reason: reason || "لغو استثنا" },
  });
}

export async function getDayPlan(date?: string): Promise<DayShiftPlanItem[]> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  return api<DayShiftPlanItem[]>(`/hr/shifts/plan${qs}`);
}

export async function setAttendanceCode(personId: string, code: string): Promise<{ success: true }> {
  return api<{ success: true }>("/hr/attendance/code", { method: "POST", body: { personId, code } });
}

export async function punchAttendance(
  body: PunchInput,
): Promise<{ punch: AttendancePunch; personName: string; punchType: "in" | "out"; punchTime: string }> {
  return api<{ punch: AttendancePunch; personName: string; punchType: "in" | "out"; punchTime: string }>(
    "/hr/attendance/punch",
    { method: "POST", body },
  );
}

export async function getTodayAttendance(date?: string): Promise<TodayAttendanceSummary> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  return api<TodayAttendanceSummary>(`/hr/attendance/today${qs}`);
}

export async function proposePunchCorrection(
  body: ProposePunchCorrectionInput,
): Promise<{ id: string; status: "pending" }> {
  return api<{ id: string; status: "pending" }>("/hr/attendance/corrections", { method: "POST", body });
}

export async function listPunchCorrections(
  date?: string,
  status?: string,
): Promise<PunchCorrectionRequest[]> {
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  if (status) params.set("status", status);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return api<PunchCorrectionRequest[]>(`/hr/attendance/corrections${qs}`);
}

export async function reviewPunchCorrection(
  id: string,
  body: ReviewPunchCorrectionInput,
): Promise<{ id: string; status: "approved" | "rejected" }> {
  return api<{ id: string; status: "approved" | "rejected" }>(`/hr/attendance/corrections/${id}/review`, {
    method: "POST",
    body,
  });
}

export async function listLeaves(
  fromDate?: string,
  toDate?: string,
  personId?: string,
): Promise<LeaveRecord[]> {
  const params = new URLSearchParams();
  if (fromDate) params.set("fromDate", fromDate);
  if (toDate) params.set("toDate", toDate);
  if (personId) params.set("personId", personId);
  const qs = params.toString() ? `?${params.toString()}` : "";
  return api<LeaveRecord[]>(`/hr/leaves${qs}`);
}

export async function recordLeave(body: RecordLeaveInput): Promise<{ id: string }> {
  return api<{ id: string }>("/hr/leaves", { method: "POST", body });
}

export async function archiveLeave(id: string, version: number, reason?: string): Promise<void> {
  await api<unknown>(`/hr/leaves/${id}/archive`, {
    body: { version, reason: reason || "لغو مرخصی" },
  });
}

export async function runUnderstaffedJob(
  date?: string,
): Promise<{ alertedShifts: string[]; notificationsSent: number }> {
  const qs = date ? `?date=${encodeURIComponent(date)}` : "";
  return api<{ alertedShifts: string[]; notificationsSent: number }>(`/hr/jobs/check-understaffed${qs}`, {
    method: "POST",
  });
}

export const hrAttendanceApi = {
  listShiftTemplates,
  createShiftTemplate,
  updateShiftTemplate,
  archiveShiftTemplate,
  listWeeklyShiftPatterns,
  getWeeklyShiftPattern,
  setWeeklyShiftPattern,
  listShiftExceptions,
  createShiftException,
  archiveShiftException,
  getDayPlan,
  setAttendanceCode,
  punchAttendance,
  getTodayAttendance,
  proposePunchCorrection,
  listPunchCorrections,
  reviewPunchCorrection,
  listLeaves,
  recordLeave,
  archiveLeave,
  runUnderstaffedJob,
};

// ---------- HR Staff Notices & Temp Workers (AG-41) ----------

import type {
  MarkTempWorkerPaidInput,
  StaffNotice,
  StaffNoticeAck,
  StaffNoticeInput,
  StaffNoticeWithStats,
  TempWorker,
  TempWorkerInput,
  TempWorkerWorkRecord,
  TempWorkerWorkRecordInput,
} from "@kitchen/schemas";

export async function listNotices(params?: {
  kind?: "notice" | "letter";
  targetType?: string;
}): Promise<StaffNoticeWithStats[]> {
  const q = new URLSearchParams();
  if (params?.kind) q.set("kind", params.kind);
  if (params?.targetType) q.set("targetType", params.targetType);
  const qs = q.toString() ? `?${q.toString()}` : "";
  return api<StaffNoticeWithStats[]>(`/hr/notices${qs}`);
}

export async function getNotice(id: string): Promise<StaffNoticeWithStats> {
  return api<StaffNoticeWithStats>(`/hr/notices/${id}`);
}

export async function createNotice(body: StaffNoticeInput): Promise<StaffNotice> {
  return api<StaffNotice>("/hr/notices", { method: "POST", body });
}

export async function ackNotice(id: string): Promise<StaffNoticeAck> {
  return api<StaffNoticeAck>(`/hr/notices/${id}/ack`, { method: "POST" });
}

export async function listTempWorkers(): Promise<TempWorker[]> {
  return api<TempWorker[]>("/hr/temp-workers");
}

export async function createTempWorker(body: TempWorkerInput): Promise<TempWorker> {
  return api<TempWorker>("/hr/temp-workers", { method: "POST", body });
}

export async function updateTempWorker(
  id: string,
  version: number,
  patch: Partial<TempWorkerInput>,
): Promise<{ version: number }> {
  return api<{ version: number }>(`/hr/temp-workers/${id}`, { method: "PUT", body: { version, ...patch } });
}

export async function listTempWorkerRecords(params?: {
  date?: string;
  workerId?: string;
}): Promise<TempWorkerWorkRecord[]> {
  const q = new URLSearchParams();
  if (params?.date) q.set("date", params.date);
  if (params?.workerId) q.set("workerId", params.workerId);
  const qs = q.toString() ? `?${q.toString()}` : "";
  return api<TempWorkerWorkRecord[]>(`/hr/temp-workers/records${qs}`);
}

export async function logTempWorkerRecord(body: TempWorkerWorkRecordInput): Promise<TempWorkerWorkRecord> {
  return api<TempWorkerWorkRecord>("/hr/temp-workers/records", { method: "POST", body });
}

export async function markTempWorkerPaid(
  id: string,
  body: MarkTempWorkerPaidInput,
): Promise<TempWorkerWorkRecord> {
  return api<TempWorkerWorkRecord>(`/hr/temp-workers/records/${id}/pay`, { method: "POST", body });
}

export const hrNoticesApi = {
  listNotices,
  getNotice,
  createNotice,
  ackNotice,
};

export const hrTempWorkersApi = {
  listTempWorkers,
  createTempWorker,
  updateTempWorker,
  listTempWorkerRecords,
  logTempWorkerRecord,
  markTempWorkerPaid,
};

// ---------- Menu Engineering (AG-42) ----------

import type {
  MenuEngineeringGroup,
  MenuEngineeringFoodItem,
  MenuEngineeringIncompleteFoodItem,
  MenuEngineeringAverages,
  MenuEngineeringGroups,
  MenuEngineeringResponse,
  MenuEngineeringQuery,
} from "@kitchen/schemas";

export async function getMenuEngineeringReport(query?: {
  from?: string | undefined;
  to?: string | undefined;
  kitchenId?: string | undefined;
}): Promise<MenuEngineeringResponse> {
  const params = new URLSearchParams();
  if (query?.from) params.set("from", query.from);
  if (query?.to) params.set("to", query.to);
  if (query?.kitchenId) params.set("kitchenId", query.kitchenId);
  const qs = params.toString();
  return api<MenuEngineeringResponse>(`/menu-engineering${qs ? `?${qs}` : ""}`);
}

export const menuEngineeringApi = {
  getMenuEngineeringReport,
};

export type {
  MenuEngineeringGroup,
  MenuEngineeringFoodItem,
  MenuEngineeringIncompleteFoodItem,
  MenuEngineeringAverages,
  MenuEngineeringGroups,
  MenuEngineeringResponse,
  MenuEngineeringQuery,
};

// ---------- ACCOUNTING (AG-34, AG-37, AG-40) ----------

import type {
  Account,
  AccountCreateInput,
  AccountLevel,
  AccountNature,
  AccountRoleMappingView,
  AccountUpdateInput,
  AccountingBackfillResult,
  AccountingDetailTurnoverEntry,
  AccountingDetailTurnoverReport,
  AccountingDetailTurnoverRow,
  AccountingJournalEntry,
  AccountingJournalLine,
  AccountingJournalReport,
  AccountingLedgerEntry,
  AccountingLedgerReport,
  AccountingRetryEvent,
  AccountingTrialBalanceReport,
  AccountingTrialBalanceRow,
  AccountingVoucher,
  AccountingVoucherCreateInput,
  AccountingVoucherLineInput,
  AccountingVoucherReverseInput,
  AccountingVoucherUpdateInput,
  FloatingDetail,
} from "@kitchen/schemas";

export async function listAccountingAccounts(): Promise<Account[]> {
  return api<Account[]>("/accounting/accounts");
}

export async function getAccountingAccount(id: string): Promise<Account> {
  return api<Account>(`/accounting/accounts/${encodeURIComponent(id)}`);
}

export async function createAccountingAccount(body: AccountCreateInput): Promise<{ id: string }> {
  return api<{ id: string }>("/accounting/accounts", { method: "POST", body });
}

export async function updateAccountingAccount(
  id: string,
  version: number,
  body: AccountUpdateInput,
): Promise<{ version: number }> {
  return api<{ version: number }>(`/accounting/accounts/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: { ...body, version },
  });
}

export async function archiveAccountingAccount(id: string, version: number): Promise<{ ok: boolean }> {
  return api<{ ok: boolean }>(`/accounting/accounts/${encodeURIComponent(id)}?version=${String(version)}`, {
    method: "DELETE",
  });
}

export async function listAccountingRoles(): Promise<AccountRoleMappingView[]> {
  return api<AccountRoleMappingView[]>("/accounting/roles");
}

export async function setAccountingRole(role: string, accountId: string): Promise<{ ok: boolean }> {
  return api<{ ok: boolean }>(`/accounting/roles/${encodeURIComponent(role)}`, {
    method: "PUT",
    body: { accountId },
  });
}

export async function ensureFloatingDetail(
  organizationId: string,
  unitId?: string | null,
): Promise<FloatingDetail> {
  return api<FloatingDetail>("/accounting/details/ensure", {
    method: "POST",
    body: { organizationId, unitId },
  });
}

export async function listAccountingVouchers(): Promise<AccountingVoucher[]> {
  return api<AccountingVoucher[]>("/accounting/vouchers");
}

export async function getAccountingVoucher(id: string): Promise<AccountingVoucher> {
  return api<AccountingVoucher>(`/accounting/vouchers/${encodeURIComponent(id)}`);
}

export async function createAccountingVoucher(body: AccountingVoucherCreateInput): Promise<{ id: string }> {
  return api<{ id: string }>("/accounting/vouchers", { method: "POST", body });
}

export async function updateAccountingVoucher(
  id: string,
  version: number,
  body: AccountingVoucherUpdateInput,
): Promise<{ version: number }> {
  return api<{ version: number }>(`/accounting/vouchers/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: { ...body, version },
  });
}

export async function deleteAccountingVoucher(id: string, version: number): Promise<{ ok: boolean }> {
  return api<{ ok: boolean }>(`/accounting/vouchers/${encodeURIComponent(id)}?version=${String(version)}`, {
    method: "DELETE",
  });
}

export async function postAccountingVoucher(id: string, version: number): Promise<AccountingVoucher> {
  return api<AccountingVoucher>(`/accounting/vouchers/${encodeURIComponent(id)}/post`, {
    method: "POST",
    body: { version },
  });
}

export async function reverseAccountingVoucher(
  id: string,
  body: AccountingVoucherReverseInput,
): Promise<AccountingVoucher> {
  return api<AccountingVoucher>(`/accounting/vouchers/${encodeURIComponent(id)}/reverse`, {
    method: "POST",
    body,
  });
}

export async function listAccountingRetryEvents(status?: string): Promise<AccountingRetryEvent[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return api<AccountingRetryEvent[]>(`/accounting/retry${qs}`);
}

export async function retryAccountingEvent(id: string): Promise<{ ok: boolean; voucherId?: string | null }> {
  return api<{ ok: boolean; voucherId?: string | null }>(`/accounting/retry/${encodeURIComponent(id)}`, {
    method: "POST",
  });
}

export async function backfillAccounting(): Promise<AccountingBackfillResult> {
  return api<AccountingBackfillResult>("/accounting/backfill", { method: "POST" });
}

export async function getJournalReport(query?: {
  fromDate?: string;
  toDate?: string;
}): Promise<AccountingJournalReport> {
  const params = new URLSearchParams();
  if (query?.fromDate) params.set("fromDate", query.fromDate);
  if (query?.toDate) params.set("toDate", query.toDate);
  const qs = params.toString();
  return api<AccountingJournalReport>(`/accounting/reports/journal${qs ? `?${qs}` : ""}`);
}

export async function getLedgerReport(query: {
  accountId: string;
  fromDate?: string;
  toDate?: string;
}): Promise<AccountingLedgerReport> {
  const params = new URLSearchParams();
  params.set("accountId", query.accountId);
  if (query.fromDate) params.set("fromDate", query.fromDate);
  if (query.toDate) params.set("toDate", query.toDate);
  return api<AccountingLedgerReport>(`/accounting/reports/ledger?${params.toString()}`);
}

export async function getTrialBalanceReport(query?: {
  fromDate?: string;
  toDate?: string;
  level?: AccountLevel;
}): Promise<AccountingTrialBalanceReport> {
  const params = new URLSearchParams();
  if (query?.fromDate) params.set("fromDate", query.fromDate);
  if (query?.toDate) params.set("toDate", query.toDate);
  if (query?.level) params.set("level", query.level);
  const qs = params.toString();
  return api<AccountingTrialBalanceReport>(`/accounting/reports/trial-balance${qs ? `?${qs}` : ""}`);
}

export async function getDetailTurnoverReport(query?: {
  organizationId?: string;
  fromDate?: string;
  toDate?: string;
}): Promise<AccountingDetailTurnoverReport> {
  const params = new URLSearchParams();
  if (query?.organizationId) params.set("organizationId", query.organizationId);
  if (query?.fromDate) params.set("fromDate", query.fromDate);
  if (query?.toDate) params.set("toDate", query.toDate);
  const qs = params.toString();
  return api<AccountingDetailTurnoverReport>(`/accounting/reports/detail-turnover${qs ? `?${qs}` : ""}`);
}

export const accountingApi = {
  listAccounts: listAccountingAccounts,
  getAccount: getAccountingAccount,
  createAccount: createAccountingAccount,
  updateAccount: updateAccountingAccount,
  archiveAccount: archiveAccountingAccount,
  listRoles: listAccountingRoles,
  setRole: setAccountingRole,
  ensureFloatingDetail,
  listVouchers: listAccountingVouchers,
  getVoucher: getAccountingVoucher,
  createVoucher: createAccountingVoucher,
  updateVoucher: updateAccountingVoucher,
  deleteVoucher: deleteAccountingVoucher,
  postVoucher: postAccountingVoucher,
  reverseVoucher: reverseAccountingVoucher,
  listRetryEvents: listAccountingRetryEvents,
  retryEvent: retryAccountingEvent,
  backfill: backfillAccounting,
  getJournalReport,
  getLedgerReport,
  getTrialBalanceReport,
  getDetailTurnoverReport,
};

export type {
  Account,
  AccountLevel,
  AccountNature,
  AccountCreateInput,
  AccountUpdateInput,
  AccountRoleMappingView,
  AccountingVoucher,
  AccountingVoucherCreateInput,
  AccountingVoucherLineInput,
  AccountingVoucherUpdateInput,
  AccountingVoucherReverseInput,
  AccountingRetryEvent,
  AccountingBackfillResult,
  AccountingJournalReport,
  AccountingJournalEntry,
  AccountingJournalLine,
  AccountingLedgerReport,
  AccountingLedgerEntry,
  AccountingTrialBalanceReport,
  AccountingTrialBalanceRow,
  AccountingDetailTurnoverReport,
  AccountingDetailTurnoverRow,
  AccountingDetailTurnoverEntry,
};

// ---------- Purchasing (lead) ----------

import type {
  PurchaseRequest,
  PurchaseRequestInput,
  Supplier,
  SupplierBillInput,
  SupplierInput,
  SupplierPaymentInput,
  SupplierStatement,
} from "@kitchen/schemas";

export interface SupplierBillRow {
  id: string;
  version: number;
  supplierId: string;
  billNumber: string;
  billDate: string;
  totalRial: number;
  requestId: string | null;
  note: string;
}

export interface SupplierPaymentRow {
  id: string;
  version: number;
  supplierId: string;
  paidOn: string;
  amountRial: number;
  method: "cash" | "card" | "transfer" | "cheque";
  reference: string;
  chequeNumber: string | null;
  chequeDueDate: string | null;
  note: string;
}

export const purchasingApi = {
  listSuppliers: () => api<Supplier[]>("/purchasing/suppliers"),
  createSupplier: (body: Partial<SupplierInput> & { name: string }) =>
    api<{ id: string }>("/purchasing/suppliers", { method: "POST", body }),
  archiveSupplier: (id: string, version: number, reason: string) =>
    api<{ ok: true }>(`/purchasing/suppliers/${id}/archive`, { method: "POST", body: { version, reason } }),
  statement: (id: string, from: string, to: string) =>
    api<SupplierStatement>(`/purchasing/suppliers/${id}/statement?from=${from}&to=${to}`),
  listRequests: () => api<PurchaseRequest[]>("/purchasing/requests"),
  createRequest: (body: Omit<PurchaseRequestInput, "note"> & { note?: string }) =>
    api<{ id: string }>("/purchasing/requests", { method: "POST", body }),
  submitRequest: (id: string, version: number) =>
    api<{ version: number }>(`/purchasing/requests/${id}/submit`, { method: "POST", body: { version } }),
  decide: (id: string, version: number, decision: "approve" | "decline", note: string) =>
    api<{ version: number }>(`/purchasing/requests/${id}/decide`, {
      method: "POST",
      body: { version, decision, note },
    }),
  cancelRequest: (id: string, version: number, reason: string) =>
    api<{ version: number }>(`/purchasing/requests/${id}/cancel`, {
      method: "POST",
      body: { version, reason },
    }),
  receive: (id: string, version: number, date: string) =>
    api<{ receiptVoucherId: string }>(`/purchasing/requests/${id}/receive`, {
      method: "POST",
      body: { version, date },
    }),
  listBills: () => api<SupplierBillRow[]>("/purchasing/bills"),
  recordBill: (body: Omit<SupplierBillInput, "note"> & { note?: string }) =>
    api<{ id: string }>("/purchasing/bills", { method: "POST", body }),
  voidBill: (id: string, version: number, reason: string) =>
    api<{ ok: true }>(`/purchasing/bills/${id}/void`, { method: "POST", body: { version, reason } }),
  listPayments: () => api<SupplierPaymentRow[]>("/purchasing/payments"),
  recordPayment: (
    body: Omit<SupplierPaymentInput, "note" | "reference"> & { note?: string; reference?: string },
  ) => api<{ id: string }>("/purchasing/payments", { method: "POST", body }),
  voidPayment: (id: string, version: number, reason: string) =>
    api<{ ok: true }>(`/purchasing/payments/${id}/void`, { method: "POST", body: { version, reason } }),
};

// ---------- Fiscal Years & Period Locks (AG-43) ----------

import type {
  CloseFiscalYearInput,
  FiscalYear,
  PeriodLock,
  ReopenFiscalYearInput,
  YearClosePreview,
  YearCloseResult,
} from "@kitchen/schemas";

export async function listFiscalYears(): Promise<FiscalYear[]> {
  return api<FiscalYear[]>("/accounting/fiscal-years");
}

export async function createFiscalYear(body: {
  year: number;
  title: string;
  startDate: string;
  endDate: string;
}): Promise<{ id: string; year: number }> {
  return api<{ id: string; year: number }>("/accounting/fiscal-years", {
    method: "POST",
    body,
  });
}

export async function listPeriodLocks(fiscalYear: number): Promise<PeriodLock[]> {
  return api<PeriodLock[]>(`/accounting/periods?fiscalYear=${String(fiscalYear)}`);
}

export async function lockPeriod(body: { fiscalYear: number; periodNumber: number }): Promise<PeriodLock> {
  return api<PeriodLock>("/accounting/periods/lock", {
    method: "POST",
    body,
  });
}

export async function unlockPeriod(body: {
  fiscalYear: number;
  periodNumber: number;
  reason: string;
}): Promise<PeriodLock> {
  return api<PeriodLock>("/accounting/periods/unlock", {
    method: "POST",
    body,
  });
}

export async function getYearClosePreview(fiscalYear: number): Promise<YearClosePreview> {
  return api<YearClosePreview>(`/accounting/fiscal-years/${String(fiscalYear)}/close-preview`);
}

export async function closeFiscalYear(
  fiscalYear: number,
  body: CloseFiscalYearInput,
): Promise<YearCloseResult> {
  return api<YearCloseResult>(`/accounting/fiscal-years/${String(fiscalYear)}/close`, {
    method: "POST",
    body,
  });
}

export async function reopenFiscalYear(
  fiscalYear: number,
  body: ReopenFiscalYearInput,
): Promise<{ ok: boolean }> {
  return api<{ ok: boolean }>(`/accounting/fiscal-years/${String(fiscalYear)}/reopen`, {
    method: "POST",
    body,
  });
}

export const fiscalYearsApi = {
  listYears: listFiscalYears,
  createYear: createFiscalYear,
  listPeriods: listPeriodLocks,
  lockPeriod,
  unlockPeriod,
  getClosePreview: getYearClosePreview,
  closeYear: closeFiscalYear,
  reopenYear: reopenFiscalYear,
};

export type {
  FiscalYear,
  PeriodLock,
  YearClosePreview,
  CloseFiscalYearInput,
  YearCloseResult,
  ReopenFiscalYearInput,
};

// ---------- HR Payroll (AG-44) ----------

import type {
  CreatePayRunInput,
  FixedBenefitDeduction,
  FixedBenefitDeductionInput,
  MonthlyWorkSummary,
  PayRulesSettings,
  PayRun,
  Payslip,
} from "@kitchen/schemas";

export async function getPayRules(): Promise<PayRulesSettings> {
  return api<PayRulesSettings>("/hr/payroll/rules");
}

export async function updatePayRules(body: PayRulesSettings): Promise<PayRulesSettings> {
  return api<PayRulesSettings>("/hr/payroll/rules", { method: "PUT", body });
}

export async function listFixedAdjustments(personId?: string): Promise<FixedBenefitDeduction[]> {
  const qs = personId ? `?personId=${encodeURIComponent(personId)}` : "";
  return api<FixedBenefitDeduction[]>(`/hr/payroll/fixed-adjustments${qs}`);
}

export async function createFixedAdjustment(
  body: FixedBenefitDeductionInput,
): Promise<FixedBenefitDeduction> {
  return api<FixedBenefitDeduction>("/hr/payroll/fixed-adjustments", { method: "POST", body });
}

export async function archiveFixedAdjustment(
  id: string,
  version: number,
  reason: string,
): Promise<{ ok: true }> {
  return api<{ ok: true }>(`/hr/payroll/fixed-adjustments/${id}/archive`, {
    method: "POST",
    body: { version, reason },
  });
}

export async function getMonthlyWorkSummary(year: number, month: number): Promise<MonthlyWorkSummary> {
  return api<MonthlyWorkSummary>(`/hr/payroll/work-summary?year=${String(year)}&month=${String(month)}`);
}

export async function listPayRuns(): Promise<PayRun[]> {
  return api<PayRun[]>("/hr/payroll/runs");
}

export async function createPayRun(body: CreatePayRunInput): Promise<PayRun> {
  return api<PayRun>("/hr/payroll/runs", { method: "POST", body });
}

export async function getPayRun(id: string): Promise<PayRun> {
  return api<PayRun>(`/hr/payroll/runs/${id}`);
}

export async function recomputePayRun(id: string): Promise<PayRun> {
  return api<PayRun>(`/hr/payroll/runs/${id}/recompute`, { method: "POST" });
}

export async function approvePayRun(id: string): Promise<PayRun> {
  return api<PayRun>(`/hr/payroll/runs/${id}/approve`, { method: "POST" });
}

export async function getPayslip(personId: string, payRunId: string): Promise<Payslip> {
  return api<Payslip>(`/hr/payroll/payslips/${personId}?payRunId=${payRunId}`);
}

export const hrPayrollApi = {
  getPayRules,
  updatePayRules,
  listFixedAdjustments,
  createFixedAdjustment,
  archiveFixedAdjustment,
  getMonthlyWorkSummary,
  listPayRuns,
  createPayRun,
  getPayRun,
  recomputePayRun,
  approvePayRun,
  getPayslip,
};

// ==========================================
// AG-45: Events module (§13)
// ==========================================

import type {
  DaySheet,
  Event,
  EventInput,
  EventStatus,
  EventTask,
  EventTaskInput,
  EventTaskUpdateInput,
  EventTeamMember,
  EventTeamMemberInput,
  EventTransitionInput,
  EventType,
  EventTypeInput,
  EventUpdateInput,
} from "@kitchen/schemas";

export async function listEvents(filters?: {
  from?: string;
  to?: string;
  status?: EventStatus;
}): Promise<Event[]> {
  const params = new URLSearchParams();
  if (filters?.from) params.set("from", filters.from);
  if (filters?.to) params.set("to", filters.to);
  if (filters?.status) params.set("status", filters.status);
  const q = params.toString();
  return api<Event[]>(`/events${q ? `?${q}` : ""}`);
}

export async function getEvent(id: string): Promise<Event & { team: EventTeamMember[]; tasks: EventTask[] }> {
  return api<Event & { team: EventTeamMember[]; tasks: EventTask[] }>(`/events/${id}`);
}

export async function createEvent(body: EventInput): Promise<Event> {
  return api<Event>("/events", { method: "POST", body });
}

export async function updateEvent(id: string, version: number, body: EventUpdateInput): Promise<Event> {
  return api<Event>(`/events/${id}`, {
    method: "PUT",
    body: { ...body, version },
  });
}

export async function transitionEventStatus(
  id: string,
  version: number,
  body: EventTransitionInput,
): Promise<Event> {
  return api<Event>(`/events/${id}/transition`, {
    method: "POST",
    body: { ...body, version },
  });
}

export async function getEventDaySheet(id: string): Promise<DaySheet> {
  return api<DaySheet>(`/events/${id}/day-sheet`);
}

export async function listEventTypes(): Promise<EventType[]> {
  return api<EventType[]>("/events/types");
}

export async function getEventType(id: string): Promise<EventType> {
  return api<EventType>(`/events/types/${id}`);
}

export async function createEventType(body: EventTypeInput): Promise<EventType> {
  return api<EventType>("/events/types", { method: "POST", body });
}

export async function updateEventType(
  id: string,
  version: number,
  body: Partial<EventTypeInput>,
): Promise<EventType> {
  return api<EventType>(`/events/types/${id}`, {
    method: "PUT",
    body: { ...body, version },
  });
}

export async function archiveEventType(id: string, version: number): Promise<{ ok: true }> {
  return api<{ ok: true }>(`/events/types/${id}/archive`, {
    method: "POST",
    body: { version },
  });
}

export async function listEventTasks(eventId: string): Promise<EventTask[]> {
  return api<EventTask[]>(`/events/${eventId}/tasks`);
}

export async function createEventTask(eventId: string, body: EventTaskInput): Promise<EventTask> {
  return api<EventTask>(`/events/${eventId}/tasks`, { method: "POST", body });
}

export async function updateEventTask(
  taskId: string,
  version: number,
  body: EventTaskUpdateInput,
): Promise<EventTask> {
  return api<EventTask>(`/events/tasks/${taskId}`, {
    method: "PUT",
    body: { ...body, version },
  });
}

export async function listEventTeamMembers(eventId: string): Promise<EventTeamMember[]> {
  return api<EventTeamMember[]>(`/events/${eventId}/team`);
}

export async function addEventTeamMember(
  eventId: string,
  body: EventTeamMemberInput,
): Promise<EventTeamMember> {
  return api<EventTeamMember>(`/events/${eventId}/team`, { method: "POST", body });
}

export async function removeEventTeamMember(eventId: string, memberId: string): Promise<{ ok: true }> {
  return api<{ ok: true }>(`/events/${eventId}/team/${memberId}/remove`, {
    method: "POST",
  });
}

export const eventsApi = {
  listEvents,
  getEvent,
  createEvent,
  updateEvent,
  transitionEventStatus,
  getEventDaySheet,
  listEventTypes,
  getEventType,
  createEventType,
  updateEventType,
  archiveEventType,
  listEventTasks,
  createEventTask,
  updateEventTask,
  listEventTeamMembers,
  addEventTeamMember,
  removeEventTeamMember,
};
